'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgDonationFill, SvgHomeHeartFill, SvgMyFill, SvgStore } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { getFloatingBnbKind } from '@/app/_components/FloatingBnbController/constants';
import { WelloPointTabItem } from '@/app/_components/WelloPointTabItem';
import { DONATION_ROUTES, MODULE_ROUTES, ROUTES, SELECTOR, SUB_DOMAIN, WEL_MARKET_ROUTES } from '@/constants';
import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';
import { useAdpopcornPlacement } from '@/modules/adpopcorn';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
type NavLinkItem = {
  kind: 'link';
  name: string;
  href: string;
  icon: typeof SvgHomeHeartFill;
  hasNotificationDot: boolean;
};
type NavAdpopcornItem = {
  kind: 'adpopcorn';
  name: '혜택';
};
type NavItem = NavLinkItem | NavAdpopcornItem;
const WELLO_NAVIGATION_ITEMS: readonly NavItem[] = [{
  kind: 'link',
  name: '홈',
  href: ROUTES.HOME.pathname,
  icon: SvgHomeHeartFill,
  hasNotificationDot: false
}, {
  kind: 'link',
  name: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  icon: SvgDonationFill,
  hasNotificationDot: false
}, {
  kind: 'link',
  name: '웰마켙',
  href: WEL_MARKET_ROUTES.HOME.pathname,
  icon: SvgStore,
  hasNotificationDot: false
}, {
  kind: 'adpopcorn',
  name: '혜택'
}, {
  kind: 'link',
  name: '마이페이지',
  href: ROUTES.MY.pathname,
  icon: SvgMyFill,
  hasNotificationDot: false
}];
const NAVIGATION_ANIMATION_DURATION = 300;
export const GlobalNavigation = () => {
  const $ = _c(12);
  const pathname = usePathname();
  const [animatedHref, setAnimatedHref] = useState(null);
  const animationTimeoutRef = useRef(null);
  const navRef = useRef(null);
  const isToss = useDeviceInfoStore(_temp);
  const currentRoute = findRoute(pathname);
  const hideBottomNav = useGlobalStore(_temp2);
  let t0;
  bb0: {
    if (hideBottomNav) {
      t0 = false;
      break bb0;
    }
    if (getFloatingBnbKind(pathname)) {
      t0 = false;
      break bb0;
    }
    t0 = currentRoute?.hasBottomNav ?? true;
  }
  const hasBottomNav = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: MESSAGE_KEYWORD.HAPTIC
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    request: requestHaptic
  } = useHandleApp(t1);
  const animateNavigationItem = href => {
    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
    }
    setAnimatedHref(href);
    animationTimeoutRef.current = window.setTimeout(() => {
      setAnimatedHref(currentHref => currentHref === href ? null : currentHref);
      animationTimeoutRef.current = null;
    }, NAVIGATION_ANIMATION_DURATION);
  };
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  usePublishElementHeight(navRef, "--bottom-nav-height");
  const isWelmarket = pathname.startsWith(WEL_MARKET_ROUTES.HOME.pathname);
  const t4 = isWelmarket ? "BOTTOM_NAV_WELMARKET" : "BOTTOM_NAV";
  let t5;
  if ($[3] !== t4) {
    t5 = {
      placement: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    isExternalWebview
  } = useAdpopcornPlacement(t5);
  let t6;
  if ($[5] !== isExternalWebview || $[6] !== isToss) {
    t6 = item => {
      if (isToss && item.kind === "link" && item.href === WEL_MARKET_ROUTES.HOME.pathname) {
        return false;
      }
      if (item.kind === "adpopcorn" && isExternalWebview) {
        return false;
      }
      return true;
    };
    $[5] = isExternalWebview;
    $[6] = isToss;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const navigationItems = WELLO_NAVIGATION_ITEMS.filter(t6);
  const t7 = "\uBA54\uC778 \uB0B4\uBE44\uAC8C\uC774\uC158";
  const t8 = cn("fixed inset-x-0 bottom-0 z-10 hidden w-full justify-center overflow-hidden bg-Gray-white mobile-tablet:flex", {
    "pb-[var(--safe-area-bottom,0px)]": hasBottomNav
  });
  const t9 = SELECTOR;
  const t10 = hasBottomNav && <ul className={cn("flex justify-around", "shadow-large h-70 w-full items-center border-t-1 border-Gray-50 px-22")}>{navigationItems.map(item_0 => {
      if (item_0.kind === "adpopcorn") {
        return <li key={item_0.name} className="flex-1"><WelloPointTabItem /></li>;
      }
      const {
        name,
        href: href_0,
        icon,
        hasNotificationDot
      } = item_0;
      const clickFnbDataset = gtmDatasets.clickFnb({
        menu_name: name
      });
      const isActive = href_0 === ROUTES.HOME.pathname ? pathname === ROUTES.HOME.pathname || pathname === MODULE_ROUTES.KB_HOME.pathname : pathname === href_0 || pathname.startsWith(`${href_0}/`);
      const isCurrentPage = pathname === href_0;
      const Icon = icon;
      return <li key={name} className="flex-1"><CustomLink aria-current={isCurrentPage ? "page" : undefined} {...clickFnbDataset} className={cn("relative flex-col gap-5 whitespace-nowrap font-500 text-Gray-550 flex-center typo-caption1", {
          "text-Gray-900 font-700 cursor-default": isActive
        })} href={href_0} onTouchStart={() => {
          if (!isCurrentPage) {
            requestHaptic();
          }
        }} onClick={e => {
          if (isCurrentPage) {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          animateNavigationItem(href_0);
        }}><div className="relative"><Icon className={cn("size-28 text-Gray-400", {
              "animate-jump animate-duration-300": animatedHref === href_0,
              "text-Gray-black": isActive
            })} />{hasNotificationDot ? <><span className="sr-only">새 소식 있음</span><div aria-hidden="true" className="absolute -right-4 -top-2 size-6 rounded-pill bg-R-500" /></> : null}</div>{name}</CustomLink></li>;
    })}</ul>;
  let t11;
  if ($[8] !== t10 || $[9] !== t8 || $[10] !== t9.GLOBAL_NAVIGATION) {
    t11 = <nav ref={navRef} aria-label={t7} className={t8} id={t9.GLOBAL_NAVIGATION}>{t10}</nav>;
    $[8] = t10;
    $[9] = t8;
    $[10] = t9.GLOBAL_NAVIGATION;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  return t11;
};
function _temp(state) {
  return state.subDomain === SUB_DOMAIN.TOSS;
}
function _temp2(state_0) {
  return state_0.hideBottomNav;
}